import type * as ThreeNamespace from 'three';
import { DEG } from '../../core/math';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Contour } from '../../core/text/flatten';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { Extrude3dValues } from './params';

/**
 * Extrudes the glyph outlines into 3D.
 *
 * The shapes come from the same flattened contours every other technique uses, so the
 * extrusion follows the exact letterforms of the chosen typeface — including variable
 * axis settings — rather than three.js's own font loader.
 *
 * Contours are sorted into outer shapes and holes by signed area, then holes are
 * assigned to whichever outer shape contains them. Without that step the counters of an
 * "O" or an "A" extrude as solid slabs.
 *
 * The renderer writes to its own canvas and `draw` blits it, which is how the WebGL
 * techniques stay compatible with the 2D compositing host.
 */
type Three = typeof ThreeNamespace;

/**
 * three.js is loaded on demand.
 *
 * Statically importing it put ~700 kB into the main bundle for everyone, including the
 * majority of compositions that never touch 3D. The progressive `step` contract makes
 * the lazy load natural: the technique simply reports "not complete" until the library
 * has arrived and the scene is built.
 */
let threePromise: Promise<Three> | null = null;
function loadThree(): Promise<Three> {
  threePromise ??= import('three');
  return threePromise;
}

export interface Extrude3dState {
  three: Three | null;
  renderer: ThreeNamespace.WebGLRenderer | null;
  scene: ThreeNamespace.Scene | null;
  camera: ThreeNamespace.PerspectiveCamera | null;
  output: OffscreenCanvas | HTMLCanvasElement;
  octx: Ctx2D;
  error: string | null;
  /** Set once the module has resolved and the scene may be constructed. */
  ready: boolean;
  built: boolean;
  triangles: number;
}

function values(ctx: TechniqueContext): Extrude3dValues {
  return ctx.values as unknown as Extrude3dValues;
}

/** Even-odd containment test used to attach holes to their outer shape. */
function contains(outer: Contour, x: number, y: number): boolean {
  const p = outer.points;
  const n = p.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = p[i * 2 + 1];
    const yj = p[j * 2 + 1];
    if (yi > y !== yj > y) {
      const xi = p[i * 2];
      const xj = p[j * 2];
      if (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

function buildShapes(THREE: Three, contours: readonly Contour[], centreX: number, centreY: number): ThreeNamespace.Shape[] {
  // TrueType outer contours and their counters wind in opposite directions; the sign of
  // the area is the reliable discriminator after the y-flip.
  const outers = contours.filter((c) => c.signedArea < 0);
  const holes = contours.filter((c) => c.signedArea >= 0);
  const shapes: ThreeNamespace.Shape[] = [];

  // If the winding convention is reversed for this font, fall back to treating the
  // largest contours as outers.
  const list = outers.length ? outers : contours;
  const holeList = outers.length ? holes : [];

  for (const contour of list) {
    const shape = new THREE.Shape();
    const p = contour.points;
    shape.moveTo(p[0] - centreX, -(p[1] - centreY));
    for (let i = 2; i < p.length; i += 2) shape.lineTo(p[i] - centreX, -(p[i + 1] - centreY));
    shape.closePath();

    for (const hole of holeList) {
      const hx = hole.points[0];
      const hy = hole.points[1];
      if (!contains(contour, hx, hy)) continue;
      const path = new THREE.Path();
      path.moveTo(hx - centreX, -(hy - centreY));
      for (let i = 2; i < hole.points.length; i += 2) {
        path.lineTo(hole.points[i] - centreX, -(hole.points[i + 1] - centreY));
      }
      path.closePath();
      shape.holes.push(path);
    }

    shapes.push(shape);
  }

  return shapes;
}

function makeMaterial(THREE: Three, v: Extrude3dValues): ThreeNamespace.Material {
  const color = new THREE.Color(v.color);
  switch (v.material) {
    case 'metal':
      return new THREE.MeshStandardMaterial({ color, metalness: Math.max(0.6, v.metalness), roughness: v.roughness * 0.5 });
    case 'glass':
      return new THREE.MeshPhysicalMaterial({
        color,
        metalness: 0,
        roughness: 0.08,
        transmission: 0.92,
        thickness: 40,
        transparent: true,
        ior: 1.45,
      });
    case 'toon':
      return new THREE.MeshToonMaterial({ color });
    case 'wireframe':
      return new THREE.MeshBasicMaterial({ color, wireframe: true });
    case 'normal':
      return new THREE.MeshNormalMaterial();
    default:
      return new THREE.MeshStandardMaterial({ color, metalness: v.metalness, roughness: v.roughness });
  }
}

export function init(ctx: TechniqueContext): Extrude3dState {
  const output = createCanvas(ctx.width, ctx.height);
  const state: Extrude3dState = {
    three: null,
    renderer: null,
    scene: null,
    camera: null,
    output,
    octx: get2d(output),
    error: null,
    ready: false,
    built: false,
    triangles: 0,
  };

  if (ctx.field.isEmpty) {
    state.built = true;
    return state;
  }

  void loadThree().then(
    (three) => {
      state.three = three;
      state.ready = true;
    },
    (error: Error) => {
      state.error = `three.js konnte nicht geladen werden: ${error.message}`;
      state.built = true;
    },
  );

  return state;
}

/** Builds the scene once the library has arrived. */
function buildScene(state: Extrude3dState, ctx: TechniqueContext): void {
  const THREE = state.three;
  if (!THREE) return;
  const v = values(ctx);

  const canvas = createCanvas(ctx.width, ctx.height);
  let renderer: ThreeNamespace.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas: canvas as HTMLCanvasElement,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    });
  } catch (error) {
    state.error = `3D nicht verfügbar: ${(error as Error).message}`;
    state.built = true;
    return;
  }

  renderer.setSize(ctx.width, ctx.height, false);
  renderer.setClearColor(0x000000, 0);
  renderer.shadowMap.enabled = v.shadows;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  const bounds = ctx.field.bounds;
  const centreX = bounds.x + bounds.width / 2;
  const centreY = bounds.y + bounds.height / 2;

  const shapes = buildShapes(THREE, ctx.field.contours, centreX, centreY);
  const geometry = new THREE.ExtrudeGeometry(shapes, {
    depth: v.depth,
    bevelEnabled: v.bevel > 0,
    bevelThickness: v.bevel,
    bevelSize: v.bevel,
    bevelSegments: v.bevelSegments,
    curveSegments: 1,
  });
  geometry.center();
  state.triangles = (geometry.getAttribute('position')?.count ?? 0) / 3;

  const mesh = new THREE.Mesh(geometry, makeMaterial(THREE, v));
  mesh.castShadow = v.shadows;
  mesh.receiveShadow = v.shadows;
  scene.add(mesh);

  // ── Lighting ─────────────────────────────────────────────────────────────
  const radius = Math.max(bounds.width, bounds.height, v.depth) * 1.6;
  scene.add(new THREE.AmbientLight(0xffffff, v.ambient));

  const key = new THREE.DirectionalLight(new THREE.Color(v.keyColor), v.keyIntensity);
  const la = v.lightAzimuth * DEG;
  key.position.set(Math.cos(la) * radius, radius * 0.9, Math.sin(la) * radius);
  key.castShadow = v.shadows;
  key.shadow.mapSize.set(1024, 1024);
  const shadowCamera = key.shadow.camera;
  shadowCamera.left = -radius;
  shadowCamera.right = radius;
  shadowCamera.top = radius;
  shadowCamera.bottom = -radius;
  shadowCamera.near = 1;
  shadowCamera.far = radius * 4;
  scene.add(key);

  const fill = new THREE.DirectionalLight(new THREE.Color(v.fillColor), v.fillIntensity);
  fill.position.set(-Math.cos(la) * radius, -radius * 0.3, -Math.sin(la) * radius);
  scene.add(fill);

  if (v.floor) {
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 8, radius * 8),
      new THREE.MeshStandardMaterial({ color: 0x1a1a20, roughness: 0.95 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -bounds.height / 2 - v.bevel - 2;
    floor.receiveShadow = v.shadows;
    scene.add(floor);
  }

  // ── Camera ───────────────────────────────────────────────────────────────
  const camera = new THREE.PerspectiveCamera(v.fov, ctx.width / ctx.height, 1, radius * 20);
  // Frame the whole block: half the diagonal over the tangent of half the FOV is the
  // distance at which it exactly fills the view, then the slider scales from there.
  const extent = Math.hypot(bounds.width, bounds.height) / 2;
  const fit = extent / Math.tan((v.fov * DEG) / 2);
  const camDistance = fit * v.distance;
  const az = v.azimuth * DEG;
  const el = v.elevation * DEG;
  camera.position.set(
    Math.cos(el) * Math.sin(az) * camDistance,
    Math.sin(el) * camDistance,
    Math.cos(el) * Math.cos(az) * camDistance,
  );
  camera.lookAt(0, 0, 0);

  state.renderer = renderer;
  state.scene = scene;
  state.camera = camera;
}

export function step(state: Extrude3dState, _dt: number, ctx: TechniqueContext): Extrude3dState {
  if (state.built) return state;
  // Still waiting for the dynamic import; report progress by staying incomplete.
  if (!state.ready) return state;
  if (!state.renderer) buildScene(state, ctx);

  if (state.renderer && state.scene && state.camera) {
    state.renderer.render(state.scene, state.camera);
    state.octx.setTransform(1, 0, 0, 1, 0, 0);
    state.octx.clearRect(0, 0, state.output.width, state.output.height);
    state.octx.drawImage(state.renderer.domElement as CanvasImageSource, 0, 0);
  }
  state.built = true;
  return state;
}

export function isComplete(state: Extrude3dState): boolean {
  return state.built;
}

export function draw(state: Extrude3dState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  if (state.error) {
    const c = target.ctx;
    c.fillStyle = 'rgba(255,180,80,0.85)';
    c.font = `${Math.max(12, target.width / 48)}px ui-sans-serif, system-ui, sans-serif`;
    c.textAlign = 'center';
    c.fillText(state.error, target.width / 2, target.height / 2);
    return;
  }
  target.ctx.globalAlpha *= v.opacity;
  target.ctx.drawImage(state.output as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: Extrude3dState): number {
  return Math.round(state.triangles);
}
